<template>
  <section class="report-chapter four-diagnosis-chapter">
    <ReportChapterTitle :title="chapterTitle || '一 中医辨识'" />

    <div class="ribbon-row">
      <ReportRibbon title="（一）中医辨识" />
    </div>

    <div class="person-row" v-if="basicInfoRows.length">
      <span v-for="item in basicInfoRows" :key="item.label">{{ item.label }}:{{ item.value }}</span>
    </div>

    <div class="photo-row" v-if="imageList.length">
      <div v-for="item in imageList" :key="item.label" class="photo-card">
        <img :src="item.url" :alt="item.label" />
        <div class="photo-label">{{ item.label }}</div>
      </div>
    </div>

    <div class="blue-divider" v-if="hasHealthPanel"></div>

    <div class="health-panel" v-if="hasHealthPanel">
      <div class="health-title" v-if="healthAnalysis.constitution">
        体质：{{ healthStatusText }}
      </div>

      <div class="health-chart" v-if="hasRiskScore">
        <div class="gauge-box">
          <div class="gauge-meter" role="img" :aria-label="riskGaugeAriaLabel">
            <img class="risk-gauge-dial" :src="riskGaugeDialImage" alt="" />
            <img
              class="risk-gauge-pointer"
              :src="riskGaugePointerImage"
              :style="riskGaugePointerStyle"
              alt=""
            />
          </div>
          <div class="gauge-tip">{{ riskAdviceText }}</div>
        </div>
      </div>
    </div>

    <div
      class="text-pair text-pair--content-only"
      v-if="healthAnalysis && healthAnalysis.pathogenesis"
    >
      <div class="text-content">{{ healthAnalysis.pathogenesis }}</div>
    </div>
    <div class="text-pair" v-if="mainSymptoms.length">
      <div class="text-label">主要表现</div>
      <div class="text-content">{{ mainSymptoms.join('') }}</div>
    </div>

    <div class="subsection" v-if="hasTongueSection">
      <ReportRibbon title="（二）舌象分析" />
      <div class="sub-title" v-if="tongueIndicators.length">1.分析指标</div>
      <div
        class="indicator-grid indicator-grid--two-columns indicator-grid--wrappable"
        v-if="tongueIndicators.length"
      >
        <div
          v-for="item in tongueIndicators"
          :key="item.label"
          class="indicator-item"
          :class="{ 'indicator-item--wide': isWideIndicator(item) }"
        >
          <span class="indicator-label">{{ item.label }}：</span>
          <span
            v-for="(part, partIndex) in getIndicatorValueParts(item.value)"
            :key="item.label + '-' + partIndex"
            :class="{ 'indicator-symbol': part.isSymbol }"
          >{{ part.text }}</span>
        </div>
      </div>

      <div class="sub-title" v-if="tongueAnalysis.length">2.异常项中医解析</div>
      <AnalysisList v-if="tongueAnalysis.length" :list="tongueAnalysis" />
    </div>

    <div class="subsection" v-if="hasFaceSection">
      <ReportRibbon title="（三）面象分析" />
      <div class="sub-title" v-if="faceIndicators.length">1.分析指标</div>
      <div
        class="indicator-grid indicator-grid--two-columns indicator-grid--wrappable"
        v-if="faceIndicators.length"
      >
        <div
          v-for="item in faceIndicators"
          :key="item.label"
          class="indicator-item"
          :class="{ 'indicator-item--wide': isWideIndicator(item) }"
        >
          <span class="indicator-label">{{ item.label }}：</span>
          <span
            v-for="(part, partIndex) in getIndicatorValueParts(item.value)"
            :key="item.label + '-' + partIndex"
            :class="{ 'indicator-symbol': part.isSymbol }"
          >{{ part.text }}</span>
        </div>
      </div>

      <div class="sub-title" v-if="faceAnalysis.length">2.异常项中医解析</div>
      <AnalysisList v-if="faceAnalysis.length" :list="faceAnalysis" />
    </div>
  </section>
</template>

<script>
import ReportRibbon from './ReportRibbon';
import ReportChapterTitle from './ReportChapterTitle';
import DiagnosisAnalysisList from './DiagnosisAnalysisList';
import {
  EMPTY_TEXT,
  clampScore,
  hasValueDeep,
  isValidValue,
  maskMobile,
  toArray,
} from '../helpers';
import { REPORT_ASSETS } from '../reportAssets';

export default {
  name: 'FourDiagnosis',
  components: {
    ReportRibbon,
    ReportChapterTitle,
    AnalysisList: DiagnosisAnalysisList,
  },
  props: {
    report: {
      type: Object,
      default: () => ({}),
    },
    chapterTitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      riskGaugeDialImage: REPORT_ASSETS.riskGaugeDial,
      riskGaugePointerImage: REPORT_ASSETS.riskGaugePointer,
    };
  },
  computed: {
    healthAnalysis() {
      return this.report.healthAnalysis || null;
    },
    faceReport() {
      return this.report.faceReport || {};
    },
    tongueReport() {
      return this.report.tongueReport || {};
    },
    hasRiskScore() {
      return isValidValue(this.healthAnalysis && this.healthAnalysis.riskVal);
    },
    riskScore() {
      if (!this.hasRiskScore) return '';
      return clampScore(this.healthAnalysis.riskVal);
    },
    healthStatusText() {
      return this.healthAnalysis.constitution;
    },
    riskAdviceText() {
      if (this.riskScore >= 80) return '状态良好，建议持续保持。';
      if (this.riskScore >= 70) return '属于中风险状态，建议您一周后再次检测。';
      return '建议及时调理并持续关注。';
    },
    riskGaugePointerStyle() {
      const score = Math.max(0, Math.min(100, Number(this.riskScore) || 0));
      const scoreRatio = score / 100;
      // The dial spans about -112° (normal) to +112° (risk) from the upward axis.
      // Its printed scale runs from 0 on the left to 100 on the right.
      const angleDegree = -112 + (scoreRatio * 224);
      return {
        transform: ['translate(-50%, -50%) rotate(', angleDegree, 'deg)'].join(''),
      };
    },
    riskGaugeAriaLabel() {
      return ['健康指数', this.riskScore, '分，', this.riskAdviceText].join('');
    },
    basicInfoRows() {
      return [
        { label: '姓名', value: this.report.userName },
        { label: '年龄', value: this.report.age },
        { label: '性别', value: this.report.gender },
        { label: '联系电话', value: maskMobile(this.report.mobile) },
      ].filter((item) => isValidValue(item.value) && item.value !== EMPTY_TEXT);
    },
    imageList() {
      return [
        { label: '舌面', url: this.tongueReport.tongueFrontImg },
        { label: '舌下', url: this.tongueReport.tongueUnderImg },
        { label: '正面象', url: this.faceReport.frontFaceImg },
        { label: '左侧面象', url: this.faceReport.leftFaceImg },
        { label: '右侧面象', url: this.faceReport.rightFaceImg },
      ].filter((item) => isValidValue(item.url));
    },
    mainSymptoms() {
      return toArray(this.healthAnalysis && this.healthAnalysis.mainSymptoms);
    },
    tongueIndicators() {
      return this.normalizeIndicators(this.tongueReport.diagnosisResult, '舌象');
    },
    faceIndicators() {
      return this.normalizeIndicators(this.faceReport.diagnosisResult, '面象');
    },
    tongueAnalysis() {
      return this.flattenList(this.tongueReport.analysis).filter((item) => hasValueDeep(item));
    },
    faceAnalysis() {
      return this.flattenList(this.faceReport.analysis).filter((item) => hasValueDeep(item));
    },
    hasHealthPanel() {
      return Boolean(
        this.healthAnalysis
          && (
            this.healthAnalysis.constitution
            || this.hasRiskScore
          ),
      );
    },
    hasTongueSection() {
      return Boolean(
        this.tongueIndicators.length
          || this.tongueAnalysis.length,
      );
    },
    hasFaceSection() {
      return Boolean(
        this.faceIndicators.length
          || this.faceAnalysis.length,
      );
    },
  },
  methods: {
    flattenList(value) {
      if (Array.isArray(value)) {
        return value.reduce((result, item) => result.concat(this.flattenList(item)), []);
      }
      return isValidValue(value) ? [value] : [];
    },
    normalizeIndicators(list, prefix) {
      return this.flattenList(list).map((item, index) => {
        if (typeof item === 'string') {
          return {
            label: prefix + (index + 1),
            value: item,
          };
        }
        return {
          label: item.name || item.label || prefix + (index + 1),
          value: this.getIndicatorValue(item),
        };
      }).filter((item) => item.value && item.value !== EMPTY_TEXT);
    },
    getIndicatorValue(item) {
      const detectResult = this.flattenList(item.detectResult)
        .map((result) => this.formatDetectResult(result))
        .filter(Boolean);
      if (detectResult.length) return detectResult.join('、');

      return item.symbol
        || item.result
        || item.value
        || item.desc
        || item.standardValue
        || '';
    },
    formatDetectResult(item) {
      if (typeof item === 'string') return item;

      const name = item.name || item.label || item.result || item.value || item.desc || '';
      const symbol = item.symbol || '';
      if (!name) return symbol;
      if (!symbol || symbol === '无' || symbol === name) return name;
      // eslint-disable-next-line prefer-template
      return name + '(' + symbol + ')';
    },
    getIndicatorValueParts(value) {
      return String(value == null ? '' : value)
        .split(/(\+)/)
        .filter(Boolean)
        .map((text) => ({
          text,
          isSymbol: text === '+',
        }));
    },
    isWideIndicator(item) {
      // 阈值：单列可容纳的字符数；超过则跨两列整行显示
      const WIDE_THRESHOLD = 14;
      const labelLen = String(item.label || '').length;
      const valueLen = String(item.value || '').length;
      return (labelLen + valueLen + 1) > WIDE_THRESHOLD;
    },
  },
};
</script>

<style lang="scss" scoped>
.ribbon-row {
  padding-bottom: 6px;
}

.person-row {
  display: grid;
  grid-template-columns: repeat(4, max-content);
  justify-content: space-between;
  gap: 0;
  padding: 8px 0;
  border-bottom: 1px solid #9e6c41;
  color: #68311d;
  font-size: 14px;
  line-height: 25px;
  font-weight: 400;
  letter-spacing: 0;
}

.person-row span {
  white-space: nowrap;
}

.photo-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 13px;
  padding: 9px 26px 12px;
}

.photo-card {
  overflow: hidden;
  border: 1px solid #9e6c41;
  background: #fff;
  text-align: center;

  img {
    display: block;
    width: 100%;
    height: 54px;
    object-fit: cover;
  }
}

.photo-label {
  height: 18px;
  color: #68311d;
  font-size: 10.5px;
  line-height: 18px;
  font-weight: 600;
}

.blue-divider {
  margin: 0 2px 6px;
  border-top: 1px solid #9e6c41;
}

.health-panel {
  padding: 8px 9px 7px;
  background: rgba(224, 190, 132, 0.22);
}

.health-title {
  margin-bottom: 5px;
  color: #68311d;
  font-size: 12px;
  line-height: 17px;
  font-weight: 700;
}

.health-chart {
  display: flex;
  justify-content: center;
  align-items: center;
}

.gauge-box {
  text-align: center;
}

.gauge-meter {
  position: relative;
  width: 112px;
  height: 76px;
  margin: 0 auto 0;
  overflow: hidden;
}

.risk-gauge-dial,
.risk-gauge-pointer {
  position: absolute;
  display: block;
  object-fit: contain;
}

.risk-gauge-dial {
  top: 0;
  left: 0;
  width: 112px;
  height: 112px;
}

.risk-gauge-pointer {
  top: 56px;
  left: 56px;
  width: 60%;
  height: auto;
  transform-origin: center;
}

.gauge-tip {
  color: #68311d;
  font-size: 7px;
  line-height: 9px;
  font-weight: 600;
}

.text-pair {
  display: grid;
  grid-template-columns: 64px 1fr;
  gap: 8px;
  margin: 8px 14px 0;
  color: #68311d;
  font-size: 14px;
  line-height: 25px;
}

.text-pair--content-only {
  grid-template-columns: minmax(0, 1fr);
}

.text-label {
  font-weight: 700;
  white-space: nowrap;
}

.text-content {
  text-align: justify;
}

.subsection {
  margin-top: 19px;
  padding-top: 1px;
}

.sub-title {
  margin: 2px 14px 6px;
  padding-bottom: 2px;
  color: #68311d;
  font-size: 13px;
  line-height: 19px;
  font-weight: 700;
}

.indicator-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 7px 10px;
  padding: 0 14px 10px;
  border-bottom: 1px solid #9e6c41;
  color: #68311d;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
}

.indicator-item {
  flex: 1 1 calc((100% - 20px) / 3);
  min-width: max-content;
  max-width: calc((100% - 10px) / 2);
  white-space: nowrap;
}

.indicator-grid--two-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  .indicator-item {
    min-width: 0;
    max-width: none;
  }
}

.indicator-grid--wrappable .indicator-item {
  overflow-wrap: anywhere;
  white-space: normal;
}

.indicator-item--wide {
  grid-column: span 2;
}

.indicator-label {
  font-weight: 700;
}

.indicator-symbol {
  color: #ff5b5b;
  font-weight: 700;
}

::v-deep .analysis-list {
  padding: 0 14px;
}

::v-deep .analysis-item {
  padding: 6px 0 10px;
  border-bottom: 1px dotted #9e6c41;
}

::v-deep .analysis-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

::v-deep .analysis-title {
  min-width: 44px;
  color: #68311d;
  font-size: 12px;
  line-height: 18px;
  font-weight: 700;
}

::v-deep .analysis-tag {
  display: inline-flex;
  align-items: center;
  min-height: 14px;
  padding: 0 4px;
  border: 1px solid #ff8d8d;
  border-radius: 2px;
  color: #ff5b5b;
  font-size: 7.5px;
  line-height: 12px;
  font-weight: 600;
}

::v-deep .analysis-body {
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 10px;
  align-items: start;

  img {
    width: 52px;
    height: 52px;
    object-fit: cover;
  }
}

::v-deep .analysis-image-caption {
  margin-top: 2px;
  color: #68311d;
  font-size: 8.5px;
  line-height: 12px;
  font-weight: 400;
}

::v-deep .analysis-body--text-only {
  grid-template-columns: 1fr;
}

::v-deep .analysis-text {
  position: relative;
  padding-left: 7px;
  color: #68311d;
  font-size: 14px;
  line-height: 25px;
  text-align: justify;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 11px;
    width: 2px;
    height: 2px;
    border-radius: 50%;
    background: #68311d;
  }

  .analysis-row-label {
    font-weight: 600;
  }
}

</style>
